import { DemoAlarmSituation } from '@hakimo-ui/hakimo/types';
import { useMemo } from 'react';
import { AlarmRow } from './AlarmRow';

export type PicksBySituation = Record<string, string[]>;

interface Props {
  selectedSituations: DemoAlarmSituation[];
  picksBySituation: PicksBySituation;
  onPicksChange: (next: PicksBySituation) => void;
  manualAlarmCount: number;
}

export function AlarmsPicker(props: Props) {
  const {
    selectedSituations,
    picksBySituation,
    onPicksChange,
    manualAlarmCount,
  } = props;

  const { totalAvailable, totalPicked } = useMemo(() => {
    let available = 0;
    let picked = 0;
    selectedSituations.forEach((s) => {
      available += s.alarms.filter((a) => a.ingestible).length;
      picked += picksBySituation[s.situation]?.length ?? 0;
    });
    return { totalAvailable: available, totalPicked: picked };
  }, [selectedSituations, picksBySituation]);

  const togglePicked = (
    situationName: string,
    alarmId: string,
    checked: boolean
  ) => {
    const current = picksBySituation[situationName] ?? [];
    const without = current.filter((id) => id !== alarmId);
    const nextForSituation = checked ? [...without, alarmId] : without;
    onPicksChange({ ...picksBySituation, [situationName]: nextForSituation });
  };

  const toggleGroup = (situation: DemoAlarmSituation, checkAll: boolean) => {
    onPicksChange({
      ...picksBySituation,
      [situation.situation]: checkAll
        ? situation.alarms
            .filter((a) => a.ingestible)
            .map((a) => a.oldRawAlarmId)
        : [],
    });
  };

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-onlight-text-2 dark:text-ondark-text-2 block text-sm">
          Alarms to ingest
        </span>
        {(totalAvailable > 0 || manualAlarmCount > 0) && (
          <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs">
            {totalPicked} of {totalAvailable} selected
            {manualAlarmCount > 0 && ` · +${manualAlarmCount} from production`}
          </span>
        )}
      </div>

      {selectedSituations.length === 0 ? (
        <div className="border-onlight-line-3 text-onlight-text-3 dark:border-ondark-line-3 dark:text-ondark-text-2 rounded border border-dashed px-4 py-8 text-center text-sm">
          Select one or more situations to see their alarms.
        </div>
      ) : (
        <div className="divide-onlight-line-2 border-onlight-line-2 dark:divide-ondark-line-2 dark:border-ondark-line-2 max-h-[60vh] divide-y overflow-y-auto rounded border">
          {selectedSituations.map((s) => {
            const ingestibleCount = s.alarms.filter((a) => a.ingestible).length;
            const groupPicks = picksBySituation[s.situation] ?? [];
            const groupPicksSet = new Set(groupPicks);
            const pickedInGroup = groupPicks.length;
            const allPicked = pickedInGroup === ingestibleCount;
            return (
              <div key={s.situation}>
                <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-onlight-text-2 dark:text-ondark-text-2 truncate text-xs font-semibold uppercase tracking-wide">
                    {s.situation}
                  </span>
                  {ingestibleCount > 0 && (
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs">
                        {pickedInGroup}/{ingestibleCount}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(s, !allPicked)}
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-xs font-medium"
                      >
                        {allPicked ? 'Clear' : 'Select all'}
                      </button>
                    </div>
                  )}
                </div>
                <ul className="divide-onlight-line-1 dark:divide-ondark-line-1 divide-y">
                  {s.alarms.map((a) => (
                    <AlarmRow
                      key={a.rawAlarmId}
                      alarm={a}
                      checked={groupPicksSet.has(a.oldRawAlarmId)}
                      onChange={(c) =>
                        togglePicked(s.situation, a.oldRawAlarmId, c)
                      }
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
